import { useEffect, useState } from 'react';
import { getIntl } from '../../../../../../utils/intl';
import { usePromptButton } from '../../../../../shared/PromptButton';
import { Action } from '../../../../../v2BaseComponents/ActionButtonLayout/types';
import { Input } from '../../../../../v2BaseComponents/Inputs';
import { ContentStack } from '../../../../../v2BaseComponents/Stack';
import { PromptActions } from '../../../../../v2BaseComponents/Prompt';
import useMedicalProfileFiles from '../../../../../../hooks/api/useMedicalProfileFiles';

const intl = getIntl('AddFolderButton', {
    promptHeader: 'Add Folder',
    folderName: 'Folder Name',
    add: 'Add',
});

export type UseAddFolderActionArgs = {
    folderId: string;
    medicalProfileId: string;
    onSetFolderId: (folderId: string) => void;
};

export type UseAddFolderActionReturnValue = {
    action: Action;
    Prompt: React.ReactNode;
};

export const useAddFolderAction = (args: UseAddFolderActionArgs): UseAddFolderActionReturnValue => {
    const [folderName, setFolderName] = useState('');
    useEffect(() => {
        setFolderName('');
    }, [args.folderId]);
    const { isCreatingFolder, createFolder } = useMedicalProfileFiles({
        medicalProfileId: args.medicalProfileId,
        folderId: args.folderId,
        onCreateFolderSuccess: ({ medicalProfileFileFolderId }) => {
            args.onSetFolderId(medicalProfileFileFolderId);
        },
    });
    const promptButton = usePromptButton({
        promptTitle: intl('promptHeader'),
        renderPromptContents: (onClose) => (
            <ContentStack>
                <Input
                    title={intl('folderName')}
                    value={folderName}
                    onChange={(e) => {
                        setFolderName(e.nativeEvent.text);
                    }}
                    testID="folder-name-input"
                />
                <PromptActions
                    actions={[
                        {
                            title: intl('add'),
                            isDisabled: !folderName || isCreatingFolder,
                            onPress: () => {
                                createFolder({ folderName, parentMedicalProfileFileFolderId: args.folderId });
                                onClose();
                            },
                            testID: 'add-folder-confirm-button',
                            icon: 'FolderPlus',
                        },
                    ]}
                />
            </ContentStack>
        ),
    });

    return {
        action: {
            title: intl('promptHeader'),
            icon: 'FolderPlus',
            onPress: () => {
                promptButton.onPress();
            },
            show: true,
            testID: 'add-folder-button',
        },
        Prompt: promptButton.Prompt,
    };
};
